/**
 * TrophyRoomPage — Displays every SEL badge a child has earned in a
 * playful grid. Tapping any earned badge replays the BadgeCelebration
 * animation. Locked badges still appear (grey / dimmed) so kids see
 * what's next to unlock.
 *
 * Route: /badges/:childId
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import BadgeCelebration from '@/components/BadgeCelebration';
import { ArrowLeft, Trophy, Sparkles, Loader2 } from 'lucide-react';

const TrophyRoomPage = () => {
  const { childId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replayBadge, setReplayBadge] = useState(null);

  useEffect(() => {
    if (!childId) return;
    (async () => {
      try {
        const res = await api.get(`/sel/trophy/${childId}`);
        setData(res.data);
      } catch (err) {
        toast.error('Could not load your trophy room.');
      } finally {
        setLoading(false);
      }
    })();
  }, [api, childId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 via-pink-50 to-sky-50">
        <Loader2 className="w-10 h-10 animate-spin text-amber-400" />
      </div>
    );
  }
  if (!data) return null;

  const earnedIds = new Set(data.badges.map((b) => b.badge_id));
  const catalog = data.catalog || [];
  const catalogWithEarnedInfo = catalog.map((c) => {
    const earned = data.badges.find((b) => b.badge_id === c.badge_id);
    return { ...c, earned: !!earned, earned_at: earned?.earned_at || null };
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-pink-50 to-sky-50">
      <BadgeCelebration badge={replayBadge} onClose={() => setReplayBadge(null)} />

      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-amber-200">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="trophy-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" />
            <h1 className="font-fredoka text-xl font-bold text-slate-800">
              {data.child_name}&apos;s Trophy Room
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Progress banner */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-6 text-center" data-testid="trophy-progress">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Badges Earned</p>
          <p className="text-4xl font-fredoka font-bold text-amber-500 mt-1">
            {data.total_earned} <span className="text-slate-300">/ {data.total_available}</span>
          </p>
          <div className="mt-3 h-2 bg-amber-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-pink-400"
              style={{ width: `${Math.min(100, (data.total_earned / Math.max(1, data.total_available)) * 100)}%` }}
            />
          </div>
        </div>

        {/* Badge grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="trophy-badge-grid">
          {catalogWithEarnedInfo.map((c) => {
            const cls = c.earned
              ? 'bg-white shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer'
              : 'bg-white/60 grayscale opacity-70 cursor-default';
            return (
              <button
                key={c.badge_id}
                type="button"
                disabled={!c.earned}
                onClick={() => c.earned && setReplayBadge({ id: c.badge_id, label: c.label, emoji: c.emoji })}
                className={`text-left rounded-2xl p-4 border border-slate-100 transition-all focus:outline-none focus:ring-2 focus:ring-amber-300 ${cls}`}
                data-testid={`trophy-badge-${c.badge_id}${c.earned ? '' : '-locked'}`}
              >
                <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${c.color} flex items-center justify-center shadow-inner mb-3`}>
                  <span className="text-4xl" role="img" aria-label={c.label}>{c.earned ? c.emoji : '🔒'}</span>
                </div>
                <p className="text-center font-fredoka font-bold text-slate-800 text-sm">{c.label}</p>
                {c.earned ? (
                  <p className="text-center text-[11px] text-emerald-600 mt-1 font-semibold flex items-center justify-center gap-1">
                    <Sparkles className="w-3 h-3" /> Tap to replay
                  </p>
                ) : (
                  <p className="text-center text-[11px] text-slate-400 mt-1">Locked</p>
                )}
              </button>
            );
          })}
        </div>

        {earnedIds.size === 0 && (
          <p className="text-center text-slate-500 mt-6">
            No badges yet — finish a lesson to earn your first one! 🎯
          </p>
        )}
      </main>
    </div>
  );
};

export default TrophyRoomPage;
